/**
 * Due tickets the server clock watches. Only pure clock events:
 * a wait that has started, or a timed reminder. Activity unlocks
 * stay client-side (Chunk 5).
 */

import { NOTIFICATION_TYPE } from '../notifications';
import { stableInboxItemId } from './inboxIds';
import { isHoldReminderAsked, normalizeReminders, REMINDER_TRIGGER } from './missionReminders';
import { isWaitFulfilled } from './waitFor';
import { projectRecurringFire } from './recurringSchedule';
import { isWaitingUntilOnly } from './waitUnlockEvents';

const MAX_TICKETS = 80;

function isoFromMs(ms) {
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function waitFireAtIso(lockState) {
  const waiting = (lockState?.blockingTasks || []).filter(
    (t) => t.reason === 'waiting_until'
  );
  const times = waiting
    .map((t) => Number(t.availableAt))
    .filter((n) => Number.isFinite(n) && n > 0);
  if (!times.length) return null;
  return isoFromMs(Math.max(...times));
}

function ticketBase(campaign, task) {
  return {
    campaignId: campaign.campaignId,
    campaignName: campaign.campaignName || '',
    breadcrumbPath: campaign.breadcrumbPath || [],
    missionId: task.id,
    missionName: task.name || '',
    missionIcon: task.icon || null,
  };
}

/**
 * @returns {Array<object>}
 */
export function collectClockTickets({
  tasks = [],
  lockStates = {},
  campaignId,
  campaignName = '',
  breadcrumbPath = [],
  now,
} = {}) {
  if (!campaignId) return [];
  const nowMs = Number.isFinite(now) ? now : Date.now();
  const campaign = { campaignId, campaignName, breadcrumbPath };
  const tickets = [];

  for (const task of tasks || []) {
    const lockState = lockStates[task.id];
    if (isWaitingUntilOnly(lockState)) {
      const fireAt = waitFireAtIso(lockState);
      if (fireAt) {
        const base = ticketBase(campaign, task);
        tickets.push({
          id: `wait:${campaignId}:${task.id}`,
          kind: 'wait',
          type: NOTIFICATION_TYPE.MISSION_AVAILABLE,
          fireAt,
          inboxId: stableInboxItemId({
            type: NOTIFICATION_TYPE.MISSION_AVAILABLE,
            campaignId,
            missionId: task.id,
          }),
          ...base,
        });
      }
    }

    for (const reminder of normalizeReminders(task.reminders)) {
      if (reminder.trigger !== REMINDER_TRIGGER.AT_TIME || !reminder.at) continue;
      if (reminder.hold) {
        if (isWaitFulfilled(task) || isHoldReminderAsked(reminder)) continue;
        const details = String(task.waitDetails || reminder.note || '').trim();
        const base = ticketBase(campaign, task);
        tickets.push({
          id: `waitfor:${campaignId}:${reminder.id}`,
          kind: 'wait_for',
          type: NOTIFICATION_TYPE.WAIT_FOR,
          fireAt: reminder.at,
          reminderId: reminder.id,
          note: details || undefined,
          inboxId: stableInboxItemId({
            type: NOTIFICATION_TYPE.WAIT_FOR,
            campaignId,
            missionId: task.id,
            reminderId: reminder.id,
            occurrenceAt: reminder.at,
          }),
          ...base,
        });
        continue;
      }
      let fireAt = reminder.at;
      let recur;
      if (reminder.recur) {
        const projected = projectRecurringFire(reminder, nowMs);
        if (projected.done || !projected.fireAt) continue;
        fireAt = projected.fireAt;
        recur = projected.recur;
      }
      const base = ticketBase(campaign, task);
      tickets.push({
        id: `reminder:${campaignId}:${reminder.id}`,
        kind: 'reminder_at_time',
        type: NOTIFICATION_TYPE.REMINDER_AT_TIME,
        fireAt,
        reminderId: reminder.id,
        note: reminder.note,
        recur,
        inboxId: stableInboxItemId({
          type: NOTIFICATION_TYPE.REMINDER_AT_TIME,
          campaignId,
          missionId: task.id,
          reminderId: reminder.id,
          occurrenceAt: recur ? fireAt : undefined,
        }),
        ...base,
      });
    }
  }

  return tickets.slice(0, MAX_TICKETS);
}

/** Stable compare key so a ticking countdown does not re-sync every second. */
export function clockTicketsSignature(tickets) {
  return (tickets || [])
    .map((t) => `${t.id}:${t.fireAt}:${t.recur?.fired ?? ''}`)
    .sort()
    .join('|');
}
